import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MagnifyingGlass, DownloadSimple, CheckCircle, XCircle, Eye, ArrowUpRight } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin, canWriteEntity, canDeleteEntity } from "@/lib/permissions";
import { formatCurrency, formatDate, todayISO } from "@/lib/format";
import { ENTITY_SCHEMAS } from "@/config/schemas";
import EditableSelect from "@/components/EditableSelect";
import MachinePlanEditor, { derivedFromPlan } from "@/components/MachinePlanEditor";
import StartOnBehalf from "@/components/StartOnBehalf";
import RunningTimers from "@/components/RunningTimers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

const REF_ENTITIES = ["jobs", "machines", "team", "operations"];

const STATUS_COLORS = {
  Active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Completed: "bg-blue-100 text-blue-700 border-blue-200",
  Delivered: "bg-violet-100 text-violet-700 border-violet-200",
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  "Under Maintenance": "bg-amber-100 text-amber-700 border-amber-200",
  Idle: "bg-slate-100 text-slate-600 border-slate-200",
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  "In Progress": "bg-blue-100 text-blue-700 border-blue-200",
  Approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function EntityPage({ entity, embedded = false }) {
  const schema = ENTITY_SCHEMAS[entity];
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const admin = isAdmin(user?.role);
  const canW = canWriteEntity(user?.role, entity);
  const canD = canDeleteEntity(user?.role, entity);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(() => localStorage.getItem("prodScope") || "today");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState(new URLSearchParams(location.search).get("q") || "");

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["entity", entity],
    queryFn: async () => (await api.get(`/${entity}`)).data,
  });

  // reference lists for entity-link dropdowns (fixed hook count)
  const jobs = useQuery({ queryKey: ["entity", "jobs"], queryFn: async () => (await api.get(`/jobs`)).data });
  const machines = useQuery({ queryKey: ["entity", "machines"], queryFn: async () => (await api.get(`/machines`)).data });
  const team = useQuery({ queryKey: ["entity", "team"], queryFn: async () => (await api.get(`/team`)).data });
  const operations = useQuery({ queryKey: ["entity", "operations"], queryFn: async () => (await api.get(`/operations`)).data });
  const refData = { jobs: jobs.data || [], machines: machines.data || [], team: team.data || [], operations: operations.data || [] };

  const refLabel = (linkEntity, id) => {
    const list = refData[linkEntity] || [];
    const found = list.find((x) => x.id === id);
    if (!found) return "—";
    const lf = ENTITY_SCHEMAS[linkEntity].labelField;
    return `${found.code} · ${found[lf] || ""}`.trim();
  };

  const openCreate = () => {
    const init = {};
    schema.fields.forEach((f) => {
      if (f.default === "today") init[f.name] = todayISO();
    });
    setForm(init);
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item);
    setOpen(true);
  };

  useEffect(() => {
    if (embedded) return;
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      openCreate();
      navigate(`/${entity}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const setField = (name, val) => setForm((p) => ({ ...p, [name]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      schema.fields.forEach((f) => {
        if (f.type === "computed" || f.type === "approval") delete payload[f.name];
      });
      if (editing) {
        const { data: saved } = await api.put(`/${entity}/${editing.id}`, payload);
        if (saved?.code_request) {
          toast.success("Changes sent to the Manager for approval — they apply once approved");
        } else {
          toast.success(`${schema.singular} updated`);
        }
      } else {
        await api.post(`/${entity}`, payload);
        toast.success(`${schema.singular} created`);
      }
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${entity}/${deleteId}`);
      toast.success(`${schema.singular} deleted`);
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleteId(null);
    }
  };

  const setApproval = async (id, status) => {
    try {
      await api.put(`/production/${id}/approval`, { status });
      toast.success(`Log marked ${status}`);
      qc.invalidateQueries({ queryKey: ["entity"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const csvValue = (item, f) => {
    const val = item[f.name];
    if (f.type === "machine_plan") {
      return (val || [])
        .filter((r) => r.machine_id)
        .map((r) => `${refLabel("machines", r.machine_id).replace(/·/g, "-")}: ${r.planned_hours || 0}h`)
        .join(" | ");
    }
    if (f.type === "entity") return refLabel(f.link.entity, val).replace(/·/g, "-");
    if (f.type === "date") return formatDate(val);
    if (f.type === "currency" || (f.type === "computed" && f.kind === "currency")) return Number(val || 0);
    if (f.type === "computed" && f.kind === "percent") return Number(val || 0);
    return val == null ? "" : String(val);
  };

  const exportCsv = () => {
    const headers = schema.fields.map((f) => `"${f.label}"`).join(",");
    const rows = filtered.map((it) =>
      schema.fields.map((f) => `"${String(csvValue(it, f)).replace(/"/g, '""')}"`).join(",")
    );
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entity}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const renderCell = (item, f) => {
    const val = item[f.name];
    if (f.type === "machine_plan") {
      const rows = (val || []).filter((r) => r.machine_id);
      if (rows.length === 0) return "—";
      const hours = rows.reduce((s, r) => s + (Number(r.planned_hours) || 0), 0);
      const ops = new Set(rows.flatMap((r) => r.operator_ids || []));
      return (
        <span className="text-slate-600 text-xs">
          {rows.length} machine{rows.length === 1 ? "" : "s"} · {hours} h · {ops.size} operator{ops.size === 1 ? "" : "s"}
        </span>
      );
    }
    if (f.type === "entity") return refLabel(f.link.entity, val);
    if (f.type === "currency" || (f.type === "computed" && f.kind === "currency")) return formatCurrency(val);
    if (f.type === "computed" && f.kind === "percent") {
      const n = Number(val || 0);
      const color = n >= 0 ? "text-emerald-600" : "text-red-600";
      return <span className={`font-semibold ${color}`}>{n}%</span>;
    }
    if (f.type === "approval") {
      const s = val || "Pending";
      return <Badge variant="outline" className={STATUS_COLORS[s] || ""}>{s}</Badge>;
    }
    if (f.type === "date") return formatDate(val);
    if (f.type === "textarea") return <span className="text-slate-500 line-clamp-1 max-w-[180px] inline-block">{val || "—"}</span>;
    if (["job_status", "status"].includes(f.name) && val) {
      return <Badge variant="outline" className={STATUS_COLORS[val] || ""}>{val}</Badge>;
    }
    return val === "" || val == null ? "—" : String(val);
  };

  useEffect(() => {
    localStorage.setItem("prodScope", scope);
  }, [scope]);

  const filtered = useMemo(() => {
    let list = items;
    if (embedded && entity === "production") {
      if (scope === "today") {
        const t = todayISO();
        list = list.filter((it) => (it.date || "").slice(0, 10) === t);
      } else if (scope === "latest") {
        list = [...list].sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))).slice(0, 10);
      }
    }
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((it) =>
      Object.values(it).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search, scope, embedded, entity]);

  const header = (
    <div className={`flex flex-col ${embedded ? "lg:flex-row lg:items-center lg:justify-between" : "sm:flex-row sm:items-center sm:justify-between"} gap-3 ${embedded ? "mb-4" : "mb-6"}`}>
      <div>
        {embedded ? (
          <h2 className="text-lg md:text-lg font-semibold text-slate-900">{schema.title}</h2>
        ) : (
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{schema.title}</h1>
        )}
        <p className="text-sm text-slate-500 mt-1">
          {filtered.length} of {items.length} record{items.length !== 1 ? "s" : ""}
          {embedded && scope === "today" ? " · today" : ""}
        </p>
        {schema.createHint && <p className="text-xs text-slate-400 mt-1 max-w-xl" data-testid={`${entity}-create-hint`}>{schema.createHint}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {embedded && entity === "production" && (
          <div className="flex rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="production-scope">
            {[["today", "Today"], ["latest", "Latest 10"], ["all", "All entries"]].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setScope(k)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${scope === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid={`production-scope-${k}`}>{label}</button>
            ))}
          </div>
        )}
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-44 sm:w-56 bg-white"
            data-testid={`${entity}-search`}
          />
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid={`${entity}-export-btn`}>
          <DownloadSimple className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
        {entity === "production" && admin && <StartOnBehalf />}
        {canW && !schema.noCreate && (
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid={`${entity}-add-btn`}>
            <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add {schema.singular}
          </Button>
        )}
        {embedded && (
          <Button variant="ghost" className="text-blue-600 hover:text-blue-800" onClick={() => navigate(`/${entity}`)}
            data-testid={`${entity}-open-module`}>
            Open full module <ArrowUpRight className="h-4 w-4 ml-1" weight="bold" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {header}
      {entity === "production" && admin && <RunningTimers />}


      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50 sticky top-0">
              <TableRow>
                {schema.fields.map((f) => (
                  <TableHead key={f.name} className="text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">
                    {f.label}
                  </TableHead>
                ))}
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={schema.fields.length + 1} className="text-center py-12 text-slate-400">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={schema.fields.length + 1} className="text-center py-12 text-slate-400">{schema.noCreate ? schema.createHint : `No records yet. Click "Add ${schema.singular}" to create one.`}</TableCell></TableRow>
              ) : (
                filtered.map((item) => (
                  <TableRow key={item.id} className="hover:bg-slate-50 transition-colors" data-testid={`${entity}-row-${item.code}`}>
                    {schema.fields.map((f) => (
                      <TableCell key={f.name} className="py-2.5 text-sm whitespace-nowrap">
                        {f.name === "code" ? <span className="font-mono font-medium text-slate-900">{renderCell(item, f)}</span> : renderCell(item, f)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right whitespace-nowrap">
                      {entity === "jobs" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/jobs/${item.id}`)} data-testid={`jobs-view-${item.code}`} title="View details">
                          <Eye className="h-4 w-4 text-blue-600" />
                        </Button>
                      )}
                      {entity === "production" && admin && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setApproval(item.id, "Approved")} data-testid={`production-approve-${item.code}`} title="Approve">
                            <CheckCircle className="h-4 w-4 text-emerald-600" weight="fill" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setApproval(item.id, "Rejected")} data-testid={`production-reject-${item.code}`} title="Reject">
                            <XCircle className="h-4 w-4 text-red-600" weight="fill" />
                          </Button>
                        </>
                      )}
                      {canW && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)} data-testid={`${entity}-edit-${item.code}`}>
                          <PencilSimple className="h-4 w-4 text-slate-600" />
                        </Button>
                      )}
                      {canD && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteId(item.id)} data-testid={`${entity}-delete-${item.code}`}>
                          <Trash className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add / Edit sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing ? `Edit ${schema.singular}` : `Add ${schema.singular}`}</SheetTitle>
            <SheetDescription>Fill in the fields below and save.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-5">
            {schema.fields.map((f) => {
              if (f.type === "computed" || f.type === "approval") {
                if (!editing) return null;
                return (
                  <div key={f.name}>
                    <Label className="text-slate-500">{f.label}{f.type === "computed" ? " (auto)" : ""}</Label>
                    <div className="mt-1 text-sm font-medium text-slate-700">{renderCell(form, f)}</div>
                  </div>
                );
              }
              if (f.name === "code" && f.readOnly && !editing) return null;
              const disabled = f.readOnly && !f.editableId;
              return (
                <div key={f.name}>
                  <Label htmlFor={f.name} className="text-slate-700 font-medium">{f.label}</Label>
                  <div className="mt-1.5">
                    {f.type === "machine_plan" ? (
                      <MachinePlanEditor
                        value={form[f.name] || []}
                        onChange={(v) => setForm((p) => ({ ...p, [f.name]: v, ...derivedFromPlan(v) }))}
                        testIdPrefix={`${entity}-mplan`}
                      />
                    ) : f.type === "select" ? (
                      <EditableSelect
                        optionsKey={f.optionsKey}
                        value={form[f.name]}
                        onChange={(v) => setField(f.name, v)}
                        testId={`${entity}-field-${f.name}`}
                      />
                    ) : f.type === "entity" ? (
                      <Select value={form[f.name] || ""} onValueChange={(v) => setField(f.name, v)}>
                        <SelectTrigger data-testid={`${entity}-field-${f.name}`}>
                          <SelectValue placeholder={`Select ${schema.singular === f.label ? "" : ""}...`} />
                        </SelectTrigger>
                        <SelectContent>
                          {(refData[f.link.entity] || []).map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.code} · {r[ENTITY_SCHEMAS[f.link.entity].labelField]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : f.type === "textarea" ? (
                      <Textarea id={f.name} value={form[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} data-testid={`${entity}-field-${f.name}`} />
                    ) : (
                      <Input
                        id={f.name}
                        type={f.type === "number" || f.type === "currency" ? "number" : f.type === "date" ? "date" : "text"}
                        value={form[f.name] ?? ""}
                        disabled={disabled}
                        onChange={(e) => setField(f.name, e.target.value)}
                        data-testid={`${entity}-field-${f.name}`}
                      />
                    )}
                    {f.editableId && editing && (
                      <p className="mt-1 text-xs text-slate-400">
                        You can rename this ID — it updates everywhere it is shown. Must stay unique.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${entity}-cancel-btn`}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid={`${entity}-save-btn`}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {schema.singular}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
